<script lang="ts">
import type { ImageProps, Image } from './types';
import type { MaybeComputedElementRef } from '@vueuse/core';
</script>

<script setup lang="ts">
import { toRef, ref, onMounted, watchEffect, computed } from 'vue';
import { useElementBounding } from '@vueuse/core';
import { useSrcset } from 'composables/useSrcset';

// Define component props with default values
const props = withDefaults(defineProps<ImageProps>(), {
  lazy: false,
  rounded: false,
  sizes: () => ({
    default: '100vw'
  })
});

const emit = defineEmits<{
  load: [];
}>();

const image = toRef(props, 'image');

const isImageObject = computed((): boolean => {
  return typeof image.value === 'object' && image.value !== null;
});

const imageObject = computed((): Image | null => {
  return isImageObject.value ? (image.value as Image) : null;
});

const imageString = computed((): string => {
  return typeof image.value === 'string' ? image.value : '';
});

const srcset = useSrcset(imageObject);
// Note: Type casting needed due to mismatch between ImageProps.sizes and srcsetSizesToAttribute expected types
const sizes = (() => {
  const sizeEntries = Object.entries(props.sizes);
  return sizeEntries
    .map(([key, value]) => {
      const prefix = key === 'default' ? '' : `(min-width: ${key}) `;
      return `${prefix}${value}`;
    })
    .join(', ');
})();
const root = ref<HTMLImageElement | null>(null);

const hasLoaded = ref(false);
const isPreloading = ref(!props.lazy);

// Handles the load event of the image
const onLoad = (): void => {
  hasLoaded.value = true;
  emit('load');
};

onMounted(() => {
  // Get the bounding position of the image element
  const { top } = useElementBounding(root.value as unknown as MaybeComputedElementRef);

  // Watch changes and trigger preloading when in viewport
  watchEffect(() => {
    if (props.lazy && !isPreloading.value && top.value) {
      isPreloading.value = hasLoaded.value || top.value < window.innerHeight * 2;
    }
  });

  // If the image is not lazy-loaded and already completed, trigger onLoad
  if (!props.lazy && (root.value as HTMLImageElement)?.complete) {
    onLoad();
  }
});
</script>

<template>
  <div
    v-if="isImageObject"
    :style="`object-position: ${imageObject?.focalX || 50}%, ${imageObject?.focalY || 50}%;`"
  >
    <NuxtImg
      ref="root"
      class="block transition-opacity duration-500"
      :class="[
        { 'opacity-0': props.lazy && !hasLoaded },
        props.rounded ? 'rounded-xl' : 'rounded-none'
      ]"
      :srcset="isPreloading ? srcset : ''"
      :sizes="sizes"
      :alt="imageObject?.description || ''"
      @load="onLoad"
    />
  </div>
  <div v-else>
    <!-- Render simple string case -->
    <img
      :src="imageString"
      alt=""
      class="block transition-opacity duration-500"
    />
  </div>
</template>
